import { Flex, FlyError, Link, Text } from "@wayflyer/flyui";
import { usePostHog } from "posthog-js/react";
import { useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";
import type { BankConnectCountryConfig } from "shared-ui";
import { useErrorModal } from "shared-ui";
import { handleContactTeam } from "shared-utils";

import type { UseBankConnectState } from "../api-types/bank_connector_settings_schemas";
import type { BankConnectionSchema, BankConnectSchema } from "../api-types/bankconnect_schemas";
import { useConsentHandler } from "../context-providers/ConsentContext";
import { useBankConnectWithBasiq } from "../elements/basiq/use-basiq-connect";
import { useBankConnectWithMocked } from "../elements/mocked/use-mocked-connect";
import { useBankConnectWithTruelayer } from "../elements/truelayer/use-truelayer-connect";
import { clearBankConnectStorage } from "../elements/use-bank-connect";
import { useSubmitAccounts } from "../elements/use-submit-accounts";

import { plaidBankConnectHookFactory } from "./plaid-bank-connect-hook-factory";

const PLAID_PROVIDER_NAME = "Plaid";
const BASIQ_PROVIDER_NAME = "Basiq";
const TRUELAYER_PROVIDER_NAME = "TrueLayer";
const MOCKED_PROVIDER_NAME = "Mocked";

export interface UseBankConnectOptions {
  onEmailCountrySelected?: (countrySelection: BankConnectCountryConfig) => void;
  onErrorOrAbandon?: () => void;
  onConnectionCallback?: () => void;
  onReconnectionCallback?: () => void;
  onAccountsSubmittedRedirectTo?: string;
}

export function useBankConnectFactory(props: BankConnectSchema, options?: UseBankConnectOptions) {
  const intl = useIntl();
  const posthog = usePostHog();
  const { openErrorModal } = useErrorModal();
  const navigate = useNavigate();
  const {
    consentNeeded,
    finaliseAccountsPath,
    basiqConnectProps,
    plaidConnectProps,
    truelayerConnectProps,
    mockedConnectProps,
  } = props;

  const {
    startConnect: startBasiqConnect,
    startReconnect: startBasiqReconnect,
    basiqClient,
    launchingBasiq,
  } = useBankConnectWithBasiq(basiqConnectProps);
  const { startConnect: startTruelayerConnect } = useBankConnectWithTruelayer(truelayerConnectProps);
  const { startConnect: startMockedConnect } = useBankConnectWithMocked(mockedConnectProps);

  const useBankConnectWithPlaid = plaidBankConnectHookFactory(
    {
      ...plaidConnectProps,
      basiqClient,
    },
    {
      onPlaidExitCallback(metadata) {
        if (
          metadata &&
          (["institution_not_found", "requires_oauth", "institution_not_supported"].includes(metadata.exit_status) ||
            metadata.error_code !== null)
        ) {
          options?.onErrorOrAbandon && options.onErrorOrAbandon();
        }
      },
      onConnectionCallback() {
        options?.onConnectionCallback?.();
      },
      onReconnectionCallback() {
        options?.onReconnectionCallback?.();
      },
    }
  );

  const {
    bankConnections,
    startConnect: startPlaidConnect,
    startReconnect: startPlaidReconnect,
    logoLoadingStates,
    providerName,
  } = useBankConnectWithPlaid();

  const { consentHandler } = useConsentHandler();

  const displayBusinessVerificationError = useCallback(() => {
    posthog.capture("basiq_business_not_verified");
    openErrorModal(
      new FlyError({
        error: new Error("Basiq Connect requires business verification"),
        title: intl.formatMessage({
          id: "bank-connect-requires-business-verification-title",
          defaultMessage: "Business Register Verification Required",
        }),
        message: (
          <Flex direction="column" gap="4">
            <FormattedMessage
              id="bank-connect-requires-business-verification-message"
              defaultMessage="<p>Open Banking regulations require that we verify your company's status in the Australian Business Register before you can connect your bank.</p><p>Please <a>contact our support team</a> with your company's ABN and registered business address to continue.</p>"
              values={{
                p: (chunks) => <Text>{chunks}</Text>,
                a: (chunks) => <Link onClick={handleContactTeam}>{chunks}</Link>,
              }}
            />
          </Flex>
        ),
        suppressLog: true,
      })
    );
  }, [openErrorModal, intl, posthog]);

  const startConnect = useCallback(
    async (countrySelection: BankConnectCountryConfig) => {
      if (countrySelection.confirmEmail) {
        return options?.onEmailCountrySelected && options.onEmailCountrySelected(countrySelection);
      }
      if (consentNeeded) {
        try {
          await consentHandler();
        } catch (error) {
          openErrorModal({ variant: "unknown", error });
        }
      }
      if (countrySelection.providerName === PLAID_PROVIDER_NAME) {
        startPlaidConnect();
      }
      if (countrySelection.providerName === BASIQ_PROVIDER_NAME) {
        if (basiqConnectProps.openBankingEnabled && basiqConnectProps.needsBusinessVerification) {
          displayBusinessVerificationError();
          return;
        }
        startBasiqConnect();
      }
      if (countrySelection.providerName === TRUELAYER_PROVIDER_NAME) {
        startTruelayerConnect(countrySelection);
      }
      if (countrySelection.providerName === MOCKED_PROVIDER_NAME) {
        startMockedConnect();
      }
    },
    [
      consentNeeded,
      options,
      consentHandler,
      openErrorModal,
      startPlaidConnect,
      startBasiqConnect,
      startTruelayerConnect,
      startMockedConnect,
      basiqConnectProps.openBankingEnabled,
      basiqConnectProps.needsBusinessVerification,
      displayBusinessVerificationError,
    ]
  );

  const startReconnectWrapper = useCallback(
    (bankConnection: BankConnectionSchema) => {
      if (bankConnection.providerType.toLowerCase() === PLAID_PROVIDER_NAME.toLowerCase()) {
        startPlaidReconnect(bankConnection);
      }
      if (bankConnection.providerType === BASIQ_PROVIDER_NAME.toLowerCase()) {
        if (basiqConnectProps.openBankingEnabled && basiqConnectProps.needsBusinessVerification) {
          displayBusinessVerificationError();
          return;
        }
        startBasiqReconnect(bankConnection);
      }
      // we don't have reconnect flows for these providers yet but
      // we can launch the regular connect flow
      if (bankConnection.providerType === TRUELAYER_PROVIDER_NAME.toLowerCase()) {
        startTruelayerConnect();
      }
      if (bankConnection.providerType === MOCKED_PROVIDER_NAME.toLowerCase()) {
        startMockedConnect();
      }
    },
    [
      startPlaidReconnect,
      startBasiqReconnect,
      startTruelayerConnect,
      startMockedConnect,
      basiqConnectProps.openBankingEnabled,
      basiqConnectProps.needsBusinessVerification,
      displayBusinessVerificationError,
    ]
  );

  const ready = !launchingBasiq;

  const useBankConnect = useCallback((): UseBankConnectState => {
    return {
      bankConnections,
      startConnect: startConnect,
      startReconnect: startReconnectWrapper,
      logoLoadingStates,
      // TODO: Remove me completely when country selection comes in
      providerName,
    };
  }, [bankConnections, logoLoadingStates, providerName, startConnect, startReconnectWrapper]);

  const onAccountsSubmittedRedirectTo = options?.onAccountsSubmittedRedirectTo;
  const { submitAccounts, submittingAccounts } = useSubmitAccounts({
    submitAccountsPath: finaliseAccountsPath,
    onAccountsSubmitted: useCallback(() => {
      clearBankConnectStorage();
      navigate(onAccountsSubmittedRedirectTo ?? "/");
    }, [navigate, onAccountsSubmittedRedirectTo]),
  });

  return {
    ready,
    submitAccounts,
    submittingAccounts,
    useBankConnect,
  };
}
